import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Flame, Target, TrendingUp, Zap, Plus, Camera, User, Trash2 } from "lucide-react";
import { avatarSrc } from "@/lib/avatar";
import { getRankColor, getRankBadge, initials } from "./leaderboardModel";

const Stat = ({ icon: Icon, label, value, tone = "text-white" }) => (
  <div className="flex items-center justify-between text-xs">
    <span className="text-gray-400 flex items-center gap-1"><Icon className="h-3 w-3" /> {label}</span>
    <span className={`${tone} font-bold`}>{value}</span>
  </div>
);

export const MemberCard = ({ member, idx, onDelete, onEditPhoto }) => (
  <div className="relative" data-testid={`leaderboard-member-${idx}`}>
    <div className="glass-card-light rounded-2xl p-4 overflow-visible transition-all duration-300 ease-out hover:shadow-xl hover:shadow-ism-teal/10 hover:border-ism-teal/30 group">
      <button onClick={(e) => { e.stopPropagation(); onDelete(member); }}
        className="absolute top-2 left-2 p-1.5 rounded-full bg-red-500/20 text-red-400 opacity-0 group-hover:opacity-100 hover:bg-red-500/40 transition-all z-20"
        data-testid={`leaderboard-delete-${idx}`}>
        <Trash2 className="h-4 w-4" />
      </button>

      <div className={`absolute -top-3 -right-3 h-10 w-10 rounded-full bg-gradient-to-br ${getRankColor(member.rank)} flex items-center justify-center text-white font-bold text-lg shadow-lg`}>
        {getRankBadge(member.rank)}
      </div>

      <div className="relative mb-3 flex justify-center">
        <div className="relative group/avatar">
          <Avatar className="h-20 w-20 border-4 border-gray-700 group-hover:border-ism-teal/50 transition-colors duration-300">
            <AvatarImage src={avatarSrc(member.photoUrl || member.avatar)} alt={member.name} />
            <AvatarFallback className="bg-ism-teal/20 text-ism-teal text-lg font-bold">{initials(member.name)}</AvatarFallback>
          </Avatar>
          <div className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center opacity-0 group-hover/avatar:opacity-100 transition-opacity cursor-pointer"
            onClick={(e) => { e.stopPropagation(); onEditPhoto(member.memberId); }} data-testid={`leaderboard-edit-photo-${idx}`}>
            <Camera className="h-6 w-6 text-white" />
          </div>
          {member.winStreak > 0 && (
            <div className="absolute -top-2 -right-2 bg-gradient-to-br from-orange-500 to-red-600 rounded-full h-7 w-7 flex items-center justify-center shadow-lg">
              <Flame className="h-4 w-4 text-white" />
            </div>
          )}
        </div>
      </div>

      <p className="text-sm font-bold text-white mb-0.5 text-center">{member.name}</p>
      <p className="text-xs text-gray-500 mb-2 text-center">"{member.callSign}"</p>

      <div className="text-center mb-3">
        <p className={`text-2xl font-bold bg-gradient-to-r ${getRankColor(member.rank)} bg-clip-text text-transparent`}>{(member.points || 0).toLocaleString()}</p>
        <p className="text-xs text-gray-400">points</p>
      </div>

      <div className="transition-all duration-300 overflow-hidden max-h-0 opacity-0 group-hover:max-h-96 group-hover:opacity-100">
        <div className="space-y-2 pt-3 border-t border-gray-700/50">
          <Stat icon={Target} label="Deals" value={member.dealsClosedThisWeek || 0} />
          <Stat icon={TrendingUp} label="Revenue" value={`$${((member.revenueGenerated || 0) / 1000).toFixed(0)}K`} tone="text-ism-teal" />
          <Stat icon={Zap} label="Streak" value={`${member.winStreak || 0} weeks`} tone="text-orange-400" />
        </div>
      </div>
    </div>
  </div>
);

export const PlaceholderCard = ({ i, onAdd }) => (
  <div className="glass-card-light rounded-2xl p-4 border-2 border-dashed border-gray-700 flex flex-col items-center justify-center min-h-[200px] cursor-pointer hover:border-ism-teal/50 transition-colors"
    onClick={onAdd} data-testid={`placeholder-${i}`}>
    <div className="w-20 h-20 rounded-full bg-gray-800 flex items-center justify-center mb-3"><User className="h-10 w-10 text-gray-600" /></div>
    <p className="text-gray-500 text-sm mb-2">Add Team Member</p>
    <Button size="sm" variant="outline" className="border-gray-700 text-gray-400 hover:border-ism-teal hover:text-ism-teal"><Plus className="h-4 w-4 mr-1" /> Add</Button>
  </div>
);
